import {
  Alert,
  Anchor,
  Badge,
  Card,
  Center,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconSearch,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { PageHeader } from '@/components/page-header';
import { ApiError, NetworkError } from '@/lib/api-error';
import {
  listUsers,
  providersOf,
  type UserFilter,
  type UserPage,
} from '@/lib/users';
import classes from './users-page.module.css';

const PAGE_SIZE = 25;

const date = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const format = new Intl.NumberFormat('en-GB');

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/** A date, or an em dash. Never an empty cell, which reads as a broken render. */
function on(value: string | null): string {
  return value ? date.format(new Date(value)) : '—';
}

const FILTERS: { value: UserFilter; label: string }[] = [
  { value: 'registered', label: 'Accounts' },
  { value: 'anonymous', label: 'Anonymous' },
  { value: 'all', label: 'All' },
];

/**
 * Everyone on the platform.
 *
 * Opens on accounts rather than on everything. Anonymous rows outnumber
 * accounts and always will — a first run is a device starting out, and
 * docs/onboarding.md is built around most of them never registering — so
 * defaulting to the whole table would bury what somebody came to look at.
 * Seeing all of it is a click, which makes the larger personal-data surface
 * something asked for rather than the default.
 *
 * Read-only, deliberately. There is nothing to edit about a person here, and
 * docs/analytics.md's note that this access "belongs in the audit trail" is not
 * yet honoured by anything — no audit table exists. Looking is unrecorded;
 * changing would be unrecorded too, which is a worse thing to leave unbuilt.
 */
export function UsersPage() {
  const [data, setData] = useState<UserPage | null>(null);
  const [status, setStatus] = useState<UserFilter>('registered');
  const [page, setPage] = useState(1);
  // Held apart from the term the query uses, so typing does not fire a request
  // per keystroke. The search runs on submit.
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const result = await listUsers({
          status,
          q: query,
          page,
          pageSize: PAGE_SIZE,
        });
        if (cancelled) return;
        setData(result);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [status, query, page]);

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const header = (
    <PageHeader
      title="Users"
      description="Everyone who has opened the app, and the few who have an account."
      actions={
        <div className={classes.controls}>
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Show
            </Text>
            <SegmentedControl
              size="sm"
              value={status}
              onChange={(value) => {
                setStatus(value as UserFilter);
                // A filter change means the old page number is about a
                // different list, and page 9 of a shorter one is empty.
                setPage(1);
              }}
              data={FILTERS}
            />
          </Stack>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(term);
              setPage(1);
            }}
          >
            <TextInput
              size="sm"
              w={220}
              label="Search"
              placeholder="Email or name"
              value={term}
              onChange={(event) => setTerm(event.currentTarget.value)}
              leftSection={<IconSearch size={15} stroke={1.7} />}
            />
          </form>
        </div>
      }
    />
  );

  if (isLoading && data === null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the users"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {users.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconUsers size={28} stroke={1.4} />
            <Text fw={600}>
              {query ? 'Nobody matches that search' : 'No users yet'}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={440}>
              {query
                ? 'Search matches an email address or a display name. An anonymous user has neither.'
                : 'A row appears here the first time a device opens the app and reaches the API.'}
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th w={190}>Ways in</Table.Th>
                  <Table.Th w={130}>Registered</Table.Th>
                  <Table.Th w={130}>First seen</Table.Th>
                  <Table.Th w={130}>Last sign-in</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => {
                  const providers = providersOf(user);
                  return (
                    <Table.Tr key={user.id}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Anchor
                            size="sm"
                            fw={600}
                            component={Link}
                            to={`/users/${user.id}`}
                            className={
                              user.email ? undefined : classes.anonymous
                            }
                          >
                            {user.email ?? 'Anonymous'}
                          </Anchor>
                          {user.displayName && (
                            <Text size="xs" c="dimmed">
                              {user.displayName}
                            </Text>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {providers.length === 0 ? (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        ) : (
                          <Group gap={4} wrap="wrap">
                            {providers.map((provider) => (
                              <Badge
                                key={provider}
                                variant="light"
                                color="gray"
                                size="sm"
                              >
                                {provider}
                              </Badge>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={user.registeredAt ? undefined : 'dimmed'}>
                          {on(user.registeredAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{on(user.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={user.lastLoginAt ? undefined : 'dimmed'}>
                          {on(user.lastLoginAt)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="xs" c="dimmed">
          {/* Unlike the Settings screens this template came from, this one
              refetches when the filter, the search or the page changes. The
              previous page stays on screen while it does — a table that empties
              and refills reads as a bug — so this is the only thing saying a
              request is in flight. */}
          {isLoading && (
            <Loader size={12} mr={6} style={{ verticalAlign: 'middle' }} />
          )}
          {format.format(total)}{' '}
          {status === 'registered'
            ? 'account'
            : status === 'anonymous'
              ? 'anonymous user'
              : 'user'}
          {total === 1 ? '' : 's'}
          {status !== 'all' && ' matching this filter'}. A reinstall creates a
          new anonymous user, so these count devices starting out, not people.
        </Text>
        {pages > 1 && (
          <Pagination
            size="sm"
            value={page}
            onChange={setPage}
            total={pages}
            withEdges
          />
        )}
      </Group>
    </Stack>
  );
}
